import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../components/Toast";
import { widgetApi } from "../services/api";
import errorTracker from "../utils/errorTracker";
import { getErrorMessage } from "../utils/formatters";

/**
 * Custom hook to load and manage widget data
 * Handles loading state, error handling, abort signals, and navigation on errors
 *
 * @param {string} widgetId - The widget ID to load
 * @param {Object} options - Configuration options
 * @param {boolean} options.redirectOnError - Whether to redirect to /widgets on error (default: true)
 * @param {boolean} options.showErrorToast - Whether to show error toast (default: true)
 * @returns {Object} { widget, loading, error, reload }
 */
const useWidget = (widgetId, options = {}) => {
  const { redirectOnError = true, showErrorToast = true } = options;

  const navigate = useNavigate();
  const toast = useToast();

  const [widget, setWidget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadWidget = useCallback(
    async (signal) => {
      if (!widgetId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await widgetApi.getWidget(widgetId, { signal });
        setWidget(response.widget);
        setError(null);
      } catch (err) {
        // Ignore abort errors (component unmounted or request cancelled)
        if (err.name === "AbortError" || err.name === "CanceledError") {
          return;
        }

        errorTracker.captureException(err, { context: "useWidget", widgetId });

        // Provide specific error message based on the error type
        let errorMsg = getErrorMessage(err, "Failed to load widget");
        if (err.response?.status === 404) {
          errorMsg = "Widget not found. It may have been deleted.";
        }

        setError(errorMsg);

        if (showErrorToast) {
          toast.error(errorMsg);
        }

        if (redirectOnError) {
          navigate("/widgets");
        }
      } finally {
        setLoading(false);
      }
    },
    // INTENTIONAL: toast (from useToast) and navigate (from useNavigate) are stable refs.
    // Only re-run when widget ID or config options change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [widgetId, redirectOnError, showErrorToast],
  );

  useEffect(() => {
    const controller = new AbortController();
    loadWidget(controller.signal);

    return () => {
      controller.abort();
    };
  }, [loadWidget]);

  // Expose reload function for manual refresh
  // Uses a ref to track abort controller and automatically abort on unmount
  const reloadControllerRef = useRef(null);

  const reload = useCallback(() => {
    // Abort any existing reload request
    if (reloadControllerRef.current) {
      reloadControllerRef.current.abort();
    }

    // Create new controller for this reload
    const controller = new AbortController();
    reloadControllerRef.current = controller;

    loadWidget(controller.signal);
  }, [loadWidget]);

  // Clean up reload controller on unmount
  useEffect(() => {
    return () => {
      if (reloadControllerRef.current) {
        reloadControllerRef.current.abort();
      }
    };
  }, []);

  return {
    widget,
    loading,
    error,
    reload,
  };
};

export default useWidget;
